/**
 * Saúde do sistema na tela da Tecnologia (#6508): WhatsApp, robô da Equatorial, envio de
 * e-mail e funções do servidor. Cada parte vira um estado (ok, atenção, fora ou sem dado) e
 * um resumo do que está acontecendo.
 *
 *   node --experimental-strip-types --test src/lib/visaoGeral/__tests__/saudeSistema.test.ts
 */
import { HORAS_ROBO_PARADO } from './backoffice.ts';
import { motivoDoRobo, type MotivoRobo } from '../../../views/Financeiro/lib/janelaFaturamentoLista.ts';

export type EstadoSaude = 'ok' | 'atencao' | 'fora' | 'sem_dado';

const PESO: Record<EstadoSaude, number> = { ok: 0, sem_dado: 1, atencao: 2, fora: 3 };
/** O pior estado da lista. */
export const piorEstado = (estados: readonly EstadoSaude[]): EstadoSaude =>
  estados.reduce<EstadoSaude>((p, e) => (PESO[e] > PESO[p] ? e : p), 'ok');

const MIN = 60_000;
const HORA = 60 * MIN;
const idade = (iso: string | null | undefined, agora: Date) => (iso ? agora.getTime() - Date.parse(iso) : Infinity);
const nas24h = (iso: string | null | undefined, agora: Date) => idade(iso, agora) <= 24 * HORA;

// ─────────────────────────── WhatsApp ───────────────────────────

/** O batimento das conexões roda a cada 12 min: sem sinal por mais que isso, algo travou. */
export const MINUTOS_SEM_SINAL = 15;
/** Mensagem na fila de envio há mais que isso: o worker de saída não está andando. */
export const MINUTOS_ENVIO_PRESO = 10;

export interface ConexaoDoInbox {
  id: string;
  channel_type: string;
  display_name: string | null;
  status: string | null;
  last_error: string | null;
  last_seen_at: string | null;
  atendente_id: string | null;
  caiu_em: string | null;
}

export interface SituacaoDaConexao {
  id: string;
  nome: string;
  estado: EstadoSaude;
  /** Desde quando está fora ou sem sinal. */
  desde: string | null;
  motivo: string | null;
  ultimoSinal: string | null;
}

export interface EnvioDoInbox { status: string | null; created_at: string; failure_reason: string | null }

/**
 * Conexões de WhatsApp com atendente (as de teste, sem atendente, ficam de fora, como no aviso
 * de queda) e as mensagens de saída das últimas 24 h que falharam ou estão presas na fila.
 */
export function saudeDoWhatsapp(conexoes: readonly ConexaoDoInbox[], envios: readonly EnvioDoInbox[], agora: Date) {
  const doWhatsapp = conexoes.filter(c => c.channel_type.startsWith('whatsapp'));
  const monitoradas = doWhatsapp.filter(c => c.atendente_id);
  const situacoes: SituacaoDaConexao[] = monitoradas.map(c => {
    const nome = c.display_name || 'WhatsApp';
    if (c.status !== 'active' || c.caiu_em) {
      return { id: c.id, nome, estado: 'fora', desde: c.caiu_em ?? c.last_seen_at, motivo: c.last_error, ultimoSinal: c.last_seen_at };
    }
    if (idade(c.last_seen_at, agora) > MINUTOS_SEM_SINAL * MIN) {
      return { id: c.id, nome, estado: 'atencao', desde: c.last_seen_at, motivo: 'Sem sinal da instância.', ultimoSinal: c.last_seen_at };
    }
    return { id: c.id, nome, estado: 'ok', desde: null, motivo: null, ultimoSinal: c.last_seen_at };
  });
  const falhas = envios.filter(e => e.status === 'failed' && nas24h(e.created_at, agora));
  const presos = envios.filter(e => e.status === 'queued' && idade(e.created_at, agora) > MINUTOS_ENVIO_PRESO * MIN).length;
  const estado: EstadoSaude = situacoes.length === 0
    ? 'sem_dado'
    : piorEstado([...situacoes.map(s => s.estado), falhas.length || presos ? 'atencao' : 'ok']);
  return {
    estado,
    conexoes: situacoes,
    naoMonitoradas: doWhatsapp.length - monitoradas.length,
    falhasDeEnvio: falhas.length,
    motivoDaFalha: falhas.map(f => f.failure_reason).find(Boolean) ?? null,
    enviosPresos: presos,
  };
}

// ─────────────────────────── Robô da Equatorial ───────────────────────────

/** UC "processando" há mais que isso: o robô travou no meio da busca. */
export const MINUTOS_PROCESSANDO_PRESO = 60;
/** Com UC esperando, tanto tempo sem tentativa já pede atenção (parado de vez = 24 h). */
export const HORAS_SEM_ATIVIDADE_ATENCAO = 2;

export interface LinhaDaFila { status: string | null; error_details: string | null; updated_at: string | null }

/**
 * Fila de download das faturas: o que espera, o que travou no meio, o que falhou nas últimas
 * 24 h (por motivo) e a última vez que o robô mexeu numa UC (falha ou em andamento: o
 * "concluído" fica de fora porque o cron das 03h também conclui).
 */
export function saudeDoRobo(fila: readonly LinhaDaFila[], ultimaAtividade: string | null, agora: Date) {
  const pendentes = fila.filter(l => l.status === 'pendente').length;
  const presos = fila.filter(l => l.status === 'processando' && idade(l.updated_at, agora) > MINUTOS_PROCESSANDO_PRESO * MIN);
  const falhas = fila.filter(l => l.status === 'falhou' && nas24h(l.updated_at, agora));
  const concluidas = fila.filter(l => l.status === 'concluido' && nas24h(l.updated_at, agora)).length;
  const porMotivo: Partial<Record<MotivoRobo, number>> = {};
  for (const f of falhas) { const m = motivoDoRobo(f.error_details); porMotivo[m] = (porMotivo[m] ?? 0) + 1; }
  const semAtividadeMs = idade(ultimaAtividade, agora);
  const parado = pendentes > 0 && semAtividadeMs > HORAS_ROBO_PARADO * HORA;
  const estado: EstadoSaude = parado ? 'fora'
    : presos.length > 0 || (pendentes > 0 && semAtividadeMs > HORAS_SEM_ATIVIDADE_ATENCAO * HORA) ? 'atencao'
    : 'ok';
  return {
    estado,
    parado,
    pendentes,
    presos: presos.length,
    presosDesde: presos.map(p => p.updated_at).filter((v): v is string => !!v).sort()[0] ?? null,
    falhas: falhas.length,
    concluidas,
    porMotivo: (Object.entries(porMotivo) as [MotivoRobo, number][]).sort((a, b) => b[1] - a[1]),
    ultimaAtividade,
  };
}

// ─────────────────────────── E-mail ───────────────────────────

export interface EmailDeCobranca { email_cobranca_status: string | null; email_cobranca_erro: string | null; email_cobranca_em: string | null }

/**
 * E-mails de cobrança das últimas 24 h (os únicos envios que o sistema registra) e a conexão de
 * e-mail do Inbox. "Enviado" = o Resend aceitou; a entrega ao cliente não é rastreada.
 */
export function saudeDoEmail(envios: readonly EmailDeCobranca[], conexoes: readonly ConexaoDoInbox[], agora: Date) {
  const doDia = envios.filter(e => nas24h(e.email_cobranca_em, agora));
  const falhas = doDia.filter(e => e.email_cobranca_status === 'falhou');
  const motivos = new Map<string, number>();
  for (const f of falhas) { const m = (f.email_cobranca_erro || 'Sem motivo registrado').trim(); motivos.set(m, (motivos.get(m) ?? 0) + 1); }
  const ultimoEnvio = envios.filter(e => e.email_cobranca_status === 'enviado').map(e => e.email_cobranca_em)
    .filter((v): v is string => !!v).sort().pop() ?? null;
  const caixas = conexoes.filter(c => c.channel_type === 'email');
  const caixasFora = caixas.filter(c => c.status !== 'active');
  const estado: EstadoSaude = caixasFora.length ? 'fora' : falhas.length ? 'atencao' : 'ok';
  return {
    estado,
    enviados: doDia.filter(e => e.email_cobranca_status === 'enviado').length,
    falhas: falhas.length,
    motivos: [...motivos.entries()].sort((a, b) => b[1] - a[1]),
    ultimoEnvio,
    caixas: caixas.map(c => ({ nome: c.display_name || 'E-mail', ativa: c.status === 'active', motivo: c.last_error })),
  };
}

// ─────────────────────────── Funções do servidor ───────────────────────────

export interface RespostaSaudeFuncoes {
  agendamentos: { execucoes: number; falhas: { tarefa: string; falhas: number; execucoes: number; ultimaFalha: string | null; mensagem: string | null }[] } | { erro: string };
  funcoes: { configurado: false } | { configurado: true; erros: { funcao: string; erros: number; chamadas: number; status: number | null; ultimaErro: string | null }[] } | { configurado: true; erro: string };
  gerado_em: string;
}

/** Falha na última hora = problema acontecendo agora. */
export const MINUTOS_FALHA_RECENTE = 60;

/**
 * Agendamentos do banco que falharam e erros 5xx das funções nas últimas 24 h (edge
 * `saude-sistema`). Sem a resposta, o estado é "sem dado".
 */
export function saudeDasFuncoes(r: RespostaSaudeFuncoes | null, agora: Date) {
  if (!r) return { estado: 'sem_dado' as EstadoSaude, agendamentos: null, agendamentosPorMotivo: [], funcoes: null, logsConfigurados: false, ultimaFalha: null as string | null };
  const agendamentos = 'falhas' in r.agendamentos ? r.agendamentos : null;
  // Uma queda do banco derruba dezenas de tarefas com a mesma mensagem: agrupa pelo motivo.
  const porMotivo = new Map<string, { mensagem: string; falhas: number; tarefas: number; ultima: string | null }>();
  for (const x of agendamentos?.falhas ?? []) {
    const mensagem = (x.mensagem || 'sem mensagem').trim();
    const g = porMotivo.get(mensagem) ?? { mensagem, falhas: 0, tarefas: 0, ultima: null };
    g.falhas += x.falhas; g.tarefas++;
    if (x.ultimaFalha && (!g.ultima || x.ultimaFalha > g.ultima)) g.ultima = x.ultimaFalha;
    porMotivo.set(mensagem, g);
  }
  const funcoes = 'erros' in r.funcoes ? r.funcoes.erros : null;
  const datas = [
    ...(agendamentos?.falhas.map(f => f.ultimaFalha) ?? []),
    ...(funcoes?.map(f => f.ultimaErro) ?? []),
  ].filter((v): v is string => !!v).sort();
  const ultimaFalha = datas[datas.length - 1] ?? null;
  const algumaFalha = (agendamentos?.falhas.length ?? 0) + (funcoes?.length ?? 0) > 0;
  const estado: EstadoSaude = !agendamentos && !funcoes ? 'sem_dado'
    : ultimaFalha && idade(ultimaFalha, agora) <= MINUTOS_FALHA_RECENTE * MIN ? 'fora'
    : algumaFalha ? 'atencao'
    : 'ok';
  return {
    estado,
    agendamentos,
    agendamentosPorMotivo: [...porMotivo.values()].sort((a, b) => b.falhas - a.falhas),
    erroAgendamentos: 'erro' in r.agendamentos ? r.agendamentos.erro : null,
    funcoes,
    erroFuncoes: r.funcoes.configurado && 'erro' in r.funcoes ? r.funcoes.erro : null,
    logsConfigurados: r.funcoes.configurado,
    ultimaFalha,
  };
}
